import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { List, SignOut, DeviceMobile, X } from '@phosphor-icons/react'
import { useAuth } from '@/auth/context'
import { displayName } from '@/auth/authApi'
import { hasAnyRole, PHONE_ROLES } from '@/auth/roles'
import { IconButton, Logo } from '@/ui'
import { cn } from '@/lib/cn'
import { env } from '@/lib/env'
import { useNetworkStatus } from '@/api'
import { CONSOLE_SECTIONS, type ConsoleSection } from './routes'
import { useScope } from './api/appConfig'

const GROUPS: ConsoleSection['group'][] = [
  'Overview',
  'Farm gate',
  'Payments',
  'Store',
  'Processing',
  'Process',
  'Packing',
  'Quality',
  'Pack & lab',
  'Setup',
]

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { roles, user, logout } = useAuth()
  const { v1 } = useScope()
  const sections = CONSOLE_SECTIONS.filter((s) => hasAnyRole(roles, s.roles) && !(v1 && s.fullOnly))
  const canUsePhone = roles.some((r) => PHONE_ROLES.includes(r))

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center gap-3 border-b border-rule px-5">
        <Logo />
      </div>
      <div className="px-5 pb-1 pt-4">
        <div className="eyebrow">Supply chain console</div>
      </div>
      <nav aria-label="Console" className="flex-1 overflow-y-auto px-3 pb-4">
        {GROUPS.map((group) => {
          const items = sections.filter((s) => s.group === group)
          if (items.length === 0) return null
          return (
            <div key={group} className="mt-4">
              <div className="px-2 pb-1.5 font-mono text-[10.5px] font-bold uppercase tracking-eyebrow text-muted/80">
                {group}
              </div>
              <ul className="flex flex-col gap-0.5">
                {items.map((s) => (
                  <li key={s.path}>
                    <NavLink
                      to={`/c/${s.path}`}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'flex h-9 items-center gap-2.5 rounded-chip px-2 text-[14px] transition-colors',
                          isActive
                            ? 'bg-ink text-paper'
                            : 'text-ink-soft hover:bg-ink/5 hover:text-ink',
                        )
                      }
                    >
                      <s.icon size={18} aria-hidden="true" />
                      {s.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </nav>
      <div className="border-t border-rule px-4 py-3">
        {canUsePhone && (
          <Link
            to="/m"
            className="mb-2 flex items-center gap-2 rounded-chip px-1 py-1 text-[13px] text-ink-soft hover:text-ink"
          >
            <DeviceMobile aria-hidden="true" /> Open phone app
          </Link>
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-[14px] font-semibold text-ink">
              {user ? displayName(user) : '—'}
            </div>
            <div className="truncate font-mono text-[10.5px] uppercase tracking-label text-muted">
              {roles.join(' · ') || 'no role'}
            </div>
          </div>
          <IconButton label="Sign out" icon={<SignOut />} onClick={() => void logout()} />
        </div>
        <div className="mt-2 font-mono text-[10.5px] text-muted">v{env.appVersion}</div>
      </div>
    </div>
  )
}

/** Office console shell: fixed sidebar on desktop, slide-over drawer on phones. */
export function ConsoleLayout() {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const { online } = useNetworkStatus()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const current = CONSOLE_SECTIONS.find((s) => location.pathname.startsWith(`/c/${s.path}`))

  return (
    <div className="min-h-dvh bg-paper lg:pl-64">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-rule bg-surface lg:block">
        <Sidebar />
      </aside>

      {open && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
        >
          <div
            className="absolute inset-0 animate-fade-in bg-ink/40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] animate-fade-in bg-surface shadow-pop">
            <div className="absolute right-2 top-3">
              <IconButton label="Close navigation" icon={<X />} onClick={() => setOpen(false)} />
            </div>
            <Sidebar onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-rule bg-paper/95 px-3 backdrop-blur lg:hidden">
        <IconButton label="Open navigation" icon={<List />} onClick={() => setOpen(true)} />
        <span className="truncate text-[15px] font-semibold">{current?.label ?? 'Console'}</span>
      </header>

      {!online && (
        <div
          role="status"
          className="border-b border-warn/30 bg-warn-wash px-4 py-2 text-[13px] text-ink lg:px-8"
        >
          You are offline. The console needs network; lists show the last data loaded.
        </div>
      )}

      <main className="mx-auto max-w-7xl px-4 py-6 lg:px-8 lg:py-8">
        <Outlet />
      </main>
    </div>
  )
}
